/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useProductList} from '../util/product-list-context'

import {Box, Checkbox, List, ListItem} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {REFINEMENT_LEGEND} from '../../product-list'
import {CheckIcon} from '../../../components/custom-icons'

// Constants
import {IS_FAMILY_F, IS_FAMILY_MD, IS_FAMILY_FS, IS_FAMILY_NO} from '../../../constants'

/**
 * CheckboxRefinements is a React functional component that renders a set of checkbox options
 * for filtering a list of products.
 *
 * This component is memoized using React.memo, meaning it will only re-render when its props change.
 * The props are used solely for data representation, as the component's actions are derived from the
 * useProductList hook, which provides the toggleFilter function to handle filter interactions.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.filter - The filter object containing the label and values for the checkboxes.
 * @param {Array} props.selectedFilters - An array of currently selected filter values.
 *
 * @returns {JSX.Element|null} The rendered component.
 */
const CheckboxRefinements = ({filter, selectedFilters}) => {
    const {formatMessage} = useIntl()
    const {toggleFilter} = useProductList()

    // Define constants for label IDs and default messages
    const LABELS = {
        [IS_FAMILY_F]: {
            id: 'family_filter.isfamily.f.label',
            defaultMessage: 'Family Matching'
        },
        [IS_FAMILY_MD]: {
            id: 'family_filter.isfamily.md.label',
            defaultMessage: 'Mother & Daughter'
        },
        [IS_FAMILY_FS]: {
            id: 'family_filter.isfamily.fs.label',
            defaultMessage: 'Father & Sons'
        }
    }

    // Workaround to display translated display values for c_orliweb_isFamily values
    let adjustedFilter = filter
    if (filter?.attributeId === 'c_orliweb_isFamily' && filter.values) {
        adjustedFilter.values = filter.values
            .filter((filterValue) => filterValue.value !== IS_FAMILY_NO)
            .map((filterValue) => {
                const labelInfo = LABELS[filterValue.value]
                if (labelInfo) {
                    return {
                        ...filterValue,
                        label: formatMessage(labelInfo)
                    }
                }
                return filterValue // Return the original filterValue if no label is found
            })
    }

    return (
        adjustedFilter?.values?.length > 0 && (
            <Box as="fieldset">
                {/* ADA legend */}
                <legend className="sr-only">
                    {formatMessage(REFINEMENT_LEGEND, {label: filter.label})}
                </legend>

                <List display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={10}>
                    {filter.values?.map((value) => {
                        const isChecked = selectedFilters.includes(value.value)
                        // Don't display refinements with no results, unless we got there by selecting too
                        // many refinements
                        if (value.hitCount === 0 && !isChecked) return

                        return (
                            <ListItem key={value.value} margin={0}>
                                <Checkbox
                                    isChecked={isChecked}
                                    icon={<CheckIcon boxSize={'10px'} />}
                                    onChange={() =>
                                        toggleFilter(value, filter.attributeId, isChecked)
                                    }
                                    aria-label={formatMessage(
                                        isChecked ? REMOVE_FILTER : ADD_FILTER,
                                        value
                                    )}
                                >
                                    {value.label}
                                </Checkbox>
                            </ListItem>
                        )
                    })}
                </List>
            </Box>
        )
    )
}

CheckboxRefinements.propTypes = {
    filter: PropTypes.object,
    selectedFilters: PropTypes.array
}

// Memoized CheckboxRefinements to prevent unnecessary re-renders.
// Use case: accordion open / close with CheckboxRefinements
export default React.memo(CheckboxRefinements)
